import React, { useEffect, useRef } from "react";

export interface MessageAnimationProps {
  /** Main text to display */
  text: string;
  /** Optional header text (creates two-line layout) */
  headerText?: string;
  /** Text color */
  color?: string;
  /** Text shadow CSS value */
  textShadow?: string;
  /** Font size (responsive clamp) */
  fontSize?: string;
  /** Header text color (if headerText is provided) */
  headerColor?: string;
  /** Header font size (if headerText is provided) */
  headerFontSize?: string;
  /** Animation duration in seconds (default: 2) */
  duration?: number;
  /** Callback when animation completes */
  onComplete: () => void;
}

/**
 * Generic message animation component
 * Shows a message with zoom-fade animation
 * Supports both single-line and two-line (header + main) layouts
 */
const MessageAnimation: React.FC<MessageAnimationProps> = ({
  text,
  headerText,
  color = "#FFFFFF",
  textShadow = "0 2px 8px rgba(0, 0, 0, 0.8)",
  fontSize = "clamp(24px, 6vw, 48px)",
  headerColor = "#ff4444",
  headerFontSize = "clamp(20px, 4vw, 30px)",
  duration = 2,
  onComplete,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Listen for animation end event
    const handleAnimationEnd = (e: AnimationEvent) => {
      // Only handle the zoomFade animation
      if (e.animationName === "zoomFade") {
        onComplete?.();
      }
    };

    container.addEventListener("animationend", handleAnimationEnd);

    // Fallback timeout in case animationend doesn't fire
    const fallbackTimer = setTimeout(() => {
      onComplete?.();
    }, duration * 1000 + 100); // Slightly longer than animation duration

    // Force a reflow to ensure animation starts immediately
    void container.offsetWidth;

    return () => {
      container.removeEventListener("animationend", handleAnimationEnd);
      clearTimeout(fallbackTimer);
    };
  }, [duration, onComplete]);

  const hasHeader = !!headerText;

  return (
    <>
      <style>{`
        @keyframes zoomFade {
          0% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.8);
          }
          15% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
          85% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(1.1);
          }
        }

        .message-animation {
          position: fixed;
          top: 40%;
          left: 50%;
          transform: translate(-50%, -50%);
          white-space: normal;
          word-wrap: break-word;
          overflow-wrap: break-word;
          width: max-content;
          max-width: min(90vw, 800px);
          text-align: center;
          pointer-events: none;
          z-index: 2000;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
          line-height: 1.2;
          user-select: none;
          padding: 0 20px;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          animation: zoomFade ${duration}s ease-in-out;
          animation-fill-mode: forwards;
        }

        .message-content {
          text-align: center;
        }

        .message-header {
          font-weight: 700;
          margin-bottom: 4px;
          text-transform: uppercase;
          letter-spacing: 2px;
        }

        .message-text {
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 2px;
        }
      `}</style>
      <div ref={containerRef} className="message-animation">
        <div className="message-content">
          {hasHeader && (
            <div
              className="message-header"
              style={{
                fontSize: headerFontSize,
                color: headerColor,
              }}
            >
              {headerText}
            </div>
          )}
          <div
            className="message-text"
            style={{
              fontSize: fontSize,
              color: color,
              textShadow: textShadow,
            }}
          >
            {text}
          </div>
        </div>
      </div>
    </>
  );
};

export default MessageAnimation;
